// System
import React, { useEffect, useMemo } from 'react';

// Components
import { TablePagination } from '@mui/material';
import { TableBodyEx, TableContainerEx, TableEx } from '@Components/shared/tables/components';
import {
  ColumnVisibilityPresenter,
  TableHeadColumnsPresenter,
  TableHeadPresenter,
  TableRowPresenter,
} from '@Components/shared/tables/presenters';
import { LoadingScreen } from '@Components/shared/loaders/LoadingScreen';

// Utils
import { applyColumnVisibility } from '@utils/data/applyColumnVisibility';
import { formatPaginationTotal } from '@utils/data/number/localeNumberFormat';

//  Table
import { useRaindropTableColumns } from './useRaindropTableColumns';

// Redux
import { useDispatch } from 'react-redux';
import { RaindropItemDto } from '@contracts/api/dto/raindrop/RaindropDto';
import { useRaindropCampaignsQuery } from '@Src/generated.graphql';
import { useRaindropsTable } from '@Modal/hooks/raindrop/useRaindropsTable';
import { useAuthorization } from '@Modal/hooks/authorization/useAuthorization';

export const RaindropsTable: React.FC = () => {
  const dispatch = useDispatch();
  const {
    page,
    limit,
    total,
    setLimit,
    setPage,
    setTotal,
    columnVisibility,
    raindropsListFilters,
    visiblityCustomizable,
    raindropTableFilters,
  } = useRaindropsTable();
  const columns = useRaindropTableColumns();
  const { userPermissions, currentUser } = useAuthorization();
  const permissionToViewRaindrops =
    userPermissions?.includes('CAN_VIEW_RAINDROP_CAMPAIGNS') || currentUser?.isSuper;
  const { data, loading, refetch } = useRaindropCampaignsQuery({
    variables: {
      pagination: {
        page,
        limit,
      },
      // @ts-ignore
      filter: raindropTableFilters,
    },
    skip: !permissionToViewRaindrops,
  });

  const visibleColumns = useMemo(
    () => applyColumnVisibility(columns, raindropsListFilters),
    [columns, columnVisibility]
  );

  useEffect(() => {
    if (data?.raindropCampaigns?.docs) {
      const { total, limit, page } = data.raindropCampaigns;
      dispatch(setTotal(total));
      dispatch(setLimit(limit));
      dispatch(setPage(page));
    }
  }, [data?.raindropCampaigns, dispatch]);

  const header = useMemo(
    () => (
      <TableHeadPresenter>
        <TableHeadColumnsPresenter columns={visibleColumns} />
        {/* @ts-ignore */}
        <ColumnVisibilityPresenter columns={columns} content={visiblityCustomizable} />
      </TableHeadPresenter>
    ),
    [columns]
  );

  if (!permissionToViewRaindrops) return <h1>No Permission</h1>;
  if (loading) return <LoadingScreen />;

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setPage(newPage + 1));
    refetch();
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setLimit(parseInt(event.target.value, 10)));
    dispatch(setPage(1));
    refetch();
  };

  return (
    <>
      {data?.raindropCampaigns?.docs?.length === 0 ? (
        <p>No Raindrop campaigns found.</p>
      ) : (
        <TableContainerEx sx={{ height: '70vh' }}>
          <TableEx name="RaindropsList">
            {header}
            <TableBodyEx>
              {/* @ts-ignore */}
              {data?.raindropCampaigns?.docs?.map((row: RaindropItemDto) => (
                <TableRowPresenter
                  id={row.id}
                  key={row.id}
                  columns={visibleColumns}
                  row={row}
                  onClick={() => {}}
                />
              ))}
            </TableBodyEx>
          </TableEx>
        </TableContainerEx>
      )}
      <TableEx name="CustomerListFooter">
        <TablePagination
          component="div"
          count={total}
          page={page - 1}
          rowsPerPage={limit}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} of ${formatPaginationTotal(count)}`
          }
        />
      </TableEx>
    </>
  );
};
